import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Avatar, 
  Grid, 
  Stack, 
  useMediaQuery, 
  Card as MUICard, 
  CardMedia, 
  IconButton, 
  Container,
  Paper,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Language, 
  LinkedIn, 
  GitHub, 
  Twitter, 
  Instagram, 
  Email, 
  Phone, 
  Facebook, 
  LocationOn,
  Work,
  School,
  Star,
  Download,
  WhatsApp,
  ContactPhone
} from '@mui/icons-material';
import { saveContactToDevice } from '../../../utils/vCardUtils.js';
import { trackCardInteraction } from '../../../api/analyticsApi';
import { getFullUrl } from '../../../utils/urlUtils';

// Color palette - Modern dark theme with accent
const colors = {
  primary: '#1a1a2e',
  secondary: '#16213e',
  accent: '#dc2626',
  accentLight: '#ef4444',
  text: '#ffffff',
  textSecondary: '#a1a1aa',
  surface: 'rgba(26, 26, 46, 0.9)',
  surfaceLight: 'rgba(255, 255, 255, 0.05)',
  glow: '0 0 30px rgba(220, 38, 38, 0.3)',
};

const socialIconMap = {
  linkedin: <LinkedIn sx={{ color: '#0077b5' }} />,
  github: <GitHub sx={{ color: '#333' }} />,
  twitter: <Twitter sx={{ color: '#1da1f2' }} />,
  instagram: <Instagram sx={{ color: '#e4405f' }} />,
  facebook: <Facebook sx={{ color: '#1877f2' }} />,
  tiktok: <Language sx={{ color: '#000' }} />,
  website: <Language sx={{ color: '#2575fc' }} />,
  whatsapp: <WhatsApp sx={{ color: '#25d366' }} />,
};

const contactIconMap = {
  email: <Email />,
  phone: <Phone />,
  whatsapp: <WhatsApp />,
};

const getContactLink = (contact, cardHolderName) => {
  const { type, value, country } = contact;
  
  if (type === 'email') {
    return `mailto:${value}`;
  } else if (type === 'phone') {
    const prefix = country === 'SG' ? '+65' : '+91';
    return `tel:${prefix}${value}`;
  } else if (type === 'whatsapp') {
    const prefix = country === 'SG' ? '65' : '91';
    const defaultMessage = encodeURIComponent(`Hello ${cardHolderName || 'there'}, How are you? Shall we talk about your services?`);
    return `https://wa.me/${prefix}${value}?text=${defaultMessage}`;
  }
  return '#';
};

const getContactDisplay = (contact) => {
  const { type, value, country } = contact;
  
  if (type === 'email') {
    return value;
  } else if (type === 'phone' || type === 'whatsapp') {
    const prefix = country === 'SG' ? '+65' : '+91';
    return `${prefix} ${value}`;
  }
  return value;
};

// Styled Components
const MainContainer = styled(Box)({
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  minHeight: '100vh',
  color: colors.text,
      fontFamily: "'Poppins', 'Inter', 'Roboto', sans-serif",
});

const HeroSection = styled(Box)(({ theme, hascover }) => ({
  position: 'relative',
  minHeight: hascover ? '50vh' : '80vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background: hascover 
    ? 'transparent' 
    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  [theme.breakpoints.down('md')]: {
    minHeight: hascover ? '40vh' : '60vh',
    padding: theme.spacing(2),
  },
}));

const CoverPhoto = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '60vh',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  display: 'flex',
  alignItems: 'flex-end',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(180deg, transparent 0%, rgba(26, 26, 46, 0.3) 50%, rgba(26, 26, 46, 0.8) 100%)`,
    zIndex: 1,
  },
  '& > *': {
    position: 'relative',
    zIndex: 2,
  },
  [theme.breakpoints.down('md')]: {
    height: '40vh',
  },
}));

const ProfileCard = styled(Paper)(({ theme }) => ({
  background: colors.surface,
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  padding: theme.spacing(4),
  textAlign: 'center',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
  maxWidth: 500,
  width: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: 16,
  },
}));

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.0rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  background: `linear-gradient(135deg, ${colors.text} 0%, ${colors.accent} 100%)`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
  },
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
  background: colors.surface,
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
  },
}));

const GalleryCard = styled(Paper)(({ theme }) => ({
  background: colors.surface,
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
  },
}));

const ContactCard = styled(Paper)(({ theme }) => ({
  background: colors.surface,
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  padding: theme.spacing(3),
  textAlign: 'center',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
  borderRadius: 16,
  padding: theme.spacing(3),
  textAlign: 'center',
  color: colors.text,
  boxShadow: colors.glow,
}));

const CompanyCard = styled(Paper)(({ theme }) => ({
  background: colors.surface,
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  padding: theme.spacing(4),
  textAlign: 'center',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: 16,
  },
}));

// Helper function to get image URL - handles both File objects and URLs
const getImageUrl = (value) => {
    if (!value) return null;
    if (value instanceof File) return URL.createObjectURL(value);
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('blob'))) return value;
    if (typeof value === 'string') return getFullUrl(value);
    return null;
};

const ModernTheme = ({ card, isPreview, isEditMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || isPreview;

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const scale = isPreview ? 0.4 : 1;

  // Extract data
  const name = card.name || 'Your Name';
  const title = card.designation || 'Your Title';
  const companyName = card.company_name || '';
  const profileImg = getImageUrl(card.profile_picture_url) || '';
  const companyLogo = getImageUrl(card.company_logo_url) || '';
  const coverPhoto = getImageUrl(card.cover_photo_url) || '';
  const about = card.about || card.description || '';
  const website = card.website || '';
  
  const contents = Array.isArray(card.contents) ? card.contents : [];
  const gallery = Array.isArray(card.product_images) ? card.product_images : [];
  const socialLinks = Array.isArray(card.social_profiles) ? card.social_profiles : [];
  const contacts = Array.isArray(card.contacts) ? card.contacts : [];

  // Process content intelligently
  const aboutContent = contents.find(c => 
    c.field_name.toLowerCase().includes('about') || 
    c.field_name.toLowerCase().includes('bio') ||
    c.field_name.toLowerCase().includes('description')
  )?.field_value || about;

  const services = contents.filter(c => {
    const fieldName = c.field_name.toLowerCase();
    return !fieldName.includes('about') && 
           !fieldName.includes('bio') && 
           !fieldName.includes('description') &&
           !fieldName.includes('experience') &&
           !fieldName.includes('years') &&
           !fieldName.includes('project') &&
           c.field_value.length > 0;
  });

  const experienceYears = contents.find(c => 
    c.field_name.toLowerCase().includes('experience') || 
    c.field_name.toLowerCase().includes('years')
  );

  const projectCount = contents.find(c => 
    c.field_name.toLowerCase().includes('project') || 
    c.field_name.toLowerCase().includes('completed')
  );

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  return (
    <MainContainer sx={{ 
      transform: `scale(${scale})`, 
      transformOrigin: 'top left', 
      width: isPreview ? '250%' : '100%', 
      minHeight: isPreview ? '250vh' : 'auto',
      overflowX: 'hidden', 
      overflowY: isPreview ? 'visible' : 'auto' 
    }}>
      {/* Cover Photo Section */}
      {coverPhoto && (
        <CoverPhoto sx={{ backgroundImage: `url(${coverPhoto})` }}>
          {/* Clean cover photo without any text overlay */}
        </CoverPhoto>
      )}

             {/* Hero/Profile Section */}
      <HeroSection hascover={!!coverPhoto}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch" justifyContent="center">
            {/* Profile Card - Left */}
            <Grid item xs={12} md={companyLogo ? 6 : 12}>
              <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
                <ProfileCard elevation={0} sx={{ height: '100%' }}>
                  <Stack spacing={3} alignItems="center">
                    <Avatar
                      src={profileImg}
                      alt={name}
                      sx={{
                        width: isMobile ? 150 : 200,
                        height: isMobile ? 150 : 200,
                        border: `4px solid ${colors.accent}`,
                        boxShadow: colors.glow,
                        mb: 2,
                      }}
                    />
                    
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: colors.text, mb: 1 }}>
                        {name}
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.accent, fontWeight: 600, mb: 2 }}>
                        {title}
                      </Typography>
                    </Box>

                    {/* Contact Information List */}
                    {contacts.length > 0 && (
                      <Box sx={{ width: '100%', mb: 3 }}>
                        <Stack spacing={2}>
                          {contacts.slice(0, 6).map((contact, index) => (
                            <Box
                              key={contact.id || index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                borderRadius: 3,
                                background: colors.surfaceLight,
                                transition: 'all 0.3s ease',
                                cursor: ['email', 'phone', 'whatsapp'].includes(contact.type) ? 'pointer' : 'default',
                                '&:hover': {
                                  background: colors.accent + '20',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                                },
                              }}
                                              onClick={() => {
                  const link = getContactLink(contact, card.name);
                  if (link !== '#') {
                    // Track contact interaction
                    trackCardInteraction(card.slug, 'contact_click', contact.type);
                    window.open(link, contact.type === 'whatsapp' ? '_blank' : '_self');
                  }
                }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2,
                                  flexShrink: 0,
                                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                                }}
                              >
                                {React.cloneElement(contactIconMap[contact.type] || <Email />, { 
                                  sx: { fontSize: 16, color: colors.text } 
                                })}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: colors.textSecondary, 
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 0.5,
                                  }}
                                >
                                  {contact.type}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: colors.text, 
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    wordBreak: 'break-all',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {getContactDisplay(contact)}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Social Links */}
                    {socialLinks.length > 0 && (
                      <Stack direction="row" spacing={2}>
                        {socialLinks.slice(0, 5).map((profile) => (
                          <IconButton
                            key={profile.id}
                            component="a"
                            href={profile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              // Track social media interaction
                              trackCardInteraction(card.slug, 'social_click', profile.platform.toLowerCase());
                            }}
                            sx={{
                              background: colors.surfaceLight,
                              color: colors.accent,
                              border: `1px solid ${colors.accent}30`,
                              '&:hover': {
                                background: colors.accent,
                                color: colors.text,
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {socialIconMap[profile.platform.toLowerCase()] || <Language />}
                          </IconButton>
                        ))}
                      </Stack>
                    )}

                    {/* Stats */}
                    {(experienceYears || projectCount) && (
                      <Stack direction="row" spacing={4}>
                        {experienceYears && (
                          <Box textAlign="center">
                            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.accent }}>
                              {experienceYears.field_value.match(/\d+/)?.[0] || '5'}+
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                              Years Experience
                            </Typography>
                          </Box>
                        )}
                        {projectCount && (
                          <Box textAlign="center">
                            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.accent }}>
                              {projectCount.field_value.match(/\d+/)?.[0] || '50'}+
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                              Projects Completed
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </ProfileCard>
              </motion.div>
            </Grid>

            {/* Company Card - Right Side */}
            {companyLogo && (
              <Grid item xs={12} md={6}>
                <motion.div 
                  initial="hidden" 
                  animate="visible" 
                  variants={scaleIn}
                  transition={{ delay: 0.3 }}
                >
                  <CompanyCard elevation={0} sx={{ height: '100%' }}>
                    <Stack spacing={3} alignItems="center" sx={{ height: '100%', justifyContent: 'center' }}>
                      <Avatar 
                        src={companyLogo} 
                        alt="Company Logo"
                        sx={{ 
                          width: isMobile ? 150 : 200,
                          height: isMobile ? 150 : 200,
                          border: `4px solid ${colors.accent}`,
                          boxShadow: colors.glow,
                          background: colors.surface,
                          mb: 2,
                        }} 
                      />
                      {companyName && (
                        <Box textAlign="center">
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              color: colors.text, 
                              fontWeight: 700,
                              mb: 1
                            }}
                          >
                            {companyName}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: colors.accent,
                              fontWeight: 600
                            }}
                          >
                            Company
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CompanyCard>
                </motion.div>
              </Grid>
            )}
          </Grid>
        </Container>
      </HeroSection>

      {/* About Section */}
      {aboutContent && (
        <Section>
          <Container maxWidth="lg">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <SectionTitle>About Me</SectionTitle>
              <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: colors.textSecondary, 
                    lineHeight: 1.8,
                    fontSize: '1.0rem',
                  }}
                >
                  {aboutContent}
                </Typography>
              </Box>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Services Section */}
      {services.length > 0 && (
        <Section sx={{ background: colors.surfaceLight }}>
          <Container maxWidth="lg">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <SectionTitle>Services</SectionTitle>
              <Grid container spacing={4}>
                {services.map((service, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div variants={scaleIn}>
                      <ServiceCard elevation={0}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                            }}
                          >
                            <Work sx={{ color: colors.text, fontSize: 28 }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text }}>
                            {service.field_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                            {service.field_value}
                          </Typography>
                        </Stack>
                      </ServiceCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Batches Section */}
      {card.batches && card.batches.length > 0 && (
        <Section>
          <Container maxWidth="lg">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <SectionTitle>Batches</SectionTitle>
              <Grid container spacing={3}>
                {card.batches.map((batch, index) => (
                  <Grid item xs={12} sm={6} md={4} key={batch.id || index}>
                    <motion.div variants={scaleIn}>
                      <ServiceCard elevation={0}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                            }}
                          >
                            {batch.image_url ? (
                              <img
                                src={batch.image_url}
                                alt={batch.caption || 'Batch'}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <School sx={{ color: colors.text, fontSize: 28 }} />
                            )}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text }}>
                            {batch.caption || 'Batch'}
                          </Typography>
                        </Stack>
                      </ServiceCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <Section>
          <Container maxWidth="lg">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <SectionTitle>Portfolio</SectionTitle>
              <Grid container spacing={3}>
                {gallery.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={image.id || index}>
                    <motion.div variants={scaleIn}>
                      <GalleryCard elevation={0}>
                        <CardMedia
                          component="img"
                                                      image={getImageUrl(image.url)}
                          alt={image.caption || `Portfolio ${index + 1}`}
                          sx={{
                            height: 250,
                            objectFit: 'cover',
                          }}
                        />
                        {image.caption && (
                          <Box sx={{ p: 2 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                color: colors.text, 
                                fontWeight: 600,
                                textAlign: 'center'
                              }}
                            >
                              {image.caption}
                            </Typography>
                          </Box>
                        )}
                      </GalleryCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Section>
      )}

      {/* Save to Contacts Section */}
      {!isPreview && !isEditMode && (
        <Section sx={{ py: 4 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ContactPhone />}
                  onClick={() => {
                    // Track vCard download
                    trackCardInteraction(card.slug, 'vcard_download', 'save_contact');
                    const success = saveContactToDevice(card);
                    if (success) {
                      showNotification('Contact saved successfully! 📱', 'success');
                    } else {
                      showNotification('Failed to save contact. Please try again.', 'error');
                    }
                  }}
                  sx={{
                    borderColor: colors.accent,
                    color: colors.accent,
                    background: 'rgba(220, 38, 38, 0.1)',
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '1.0rem',
                    textTransform: 'none',
                    border: `2px solid ${colors.accent}`,
                    backdropFilter: 'blur(20px)',
                    '&:hover': {
                      background: colors.accent,
                      color: colors.text,
                      transform: 'translateY(-2px)',
                      boxShadow: colors.glow,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Save to Contacts
                </Button>
              </motion.div>
            </Box>
          </Container>
        </Section>
      )}

             {/* Website Section */}
       {website && (
         <Section sx={{ background: colors.surfaceLight, py: 4 }}>
           <Container maxWidth="lg">
             <Box sx={{ textAlign: 'center' }}>
               <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                 <Button
                   variant="contained"
                   size="large"
                   component="a"
                   href={website}
                   target="_blank"
                   rel="noopener noreferrer"
                   sx={{
                     background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                     color: colors.text,
                     px: 4,
                     py: 2,
                     borderRadius: 2,
                     fontWeight: 600,
                     fontSize: '1.0rem',
                     textTransform: 'none',
                     boxShadow: colors.glow,
                     '&:hover': {
                       transform: 'translateY(-2px)',
                       boxShadow: `0 8px 32px rgba(220, 38, 38, 0.4)`,
                     },
                     transition: 'all 0.3s ease',
                   }}
                 >
                   Visit Website
                 </Button>
               </motion.div>
             </Box>
           </Container>
         </Section>
       )}

       {/* Snackbar for notifications */}
       <Snackbar
         open={snackbar.open}
         autoHideDuration={4000}
         onClose={handleCloseSnackbar}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
         <Alert 
           onClose={handleCloseSnackbar} 
           severity={snackbar.severity}
           sx={{ width: '100%' }}
         >
           {snackbar.message}
         </Alert>
       </Snackbar>
    </MainContainer>
  );
};

export default ModernTheme; 